import type { z } from "zod";
import { config } from "./config";
import { supabase } from "./supabase";

/** A refusal from the API, with its stable code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Call the API as the signed-in staff member, and check the reply against its
 * contract. The access token carries the second factor (aal2); without it the
 * gateway refuses every /v1/admin route.
 */
export async function api<S extends z.ZodType>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, schema: S, body?: unknown): Promise<z.infer<S>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(`${config.apiUrl}/v1${path}`, {
    method,
    headers: {
      accept: "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? "unknown", err?.message ?? res.statusText);
  }
  return schema.parse(json);
}
